// Utilities for dynamic feature configuration.
import { FeatureMenuItem, getFeatures, getDefaultFeatureConfig } from "feature/feature-helpers";
import { ConfigContext, useGlobalProperty, useStructureKey } from "./datastore";
import { useContext } from 'react';
import { Feature } from "./stdtypes";
import { StandardConfig } from "./stdconfig";
    
export function useConfig<T>() : T {
    const config = useContext(ConfigContext);
    return config as T;
}

export function useIsReadOnly() {
    const {readOnly} = useConfig<StandardConfig>();
    return readOnly;
}

// This feature is replacing the config items already present rather than extending them

// HACK: We cast it to T[] despite the fact that it's not a T[] because otherwise 
// all configs would have to use a custom array type, which would be a pain, 
// and anything that comes back from useConfig will have gone through assembleConfig
// which converts all the REPLACE arrays back into regular arrays.
export function REPLACE<T>(array: T[]) : T[] {
    return {REPLACE: array} as any as T[];
}

// Add these items to the front of the array of items rather than the back
export function FIRST<T>(array: T[]) : T[] {
    return {FIRST: array} as any as T[];
}

export function useEnabledFeatures() {
    const structureKey = useStructureKey();
    const tunnedOnFeatures = useGlobalProperty('features');
    const defaultFeatures = getDefaultFeatureConfig()[structureKey];
    return {...defaultFeatures, ...tunnedOnFeatures};
}

export function getEnabledFeatures(datastore) {
    const structureKey = datastore?.getStructureKey();
    const turnedOnFeatures = datastore?.getGlobalProperty('features') ?? {};
    const defaultFeatures = getDefaultFeatureConfig()[structureKey] ?? {};
    return {...defaultFeatures, ...turnedOnFeatures};
}

export function noDefaultFeatures(structureKey) {
    const defaultFeatures = getDefaultFeatureConfig()[structureKey] ?? {};
    const disabledMap = {};
    Object.keys(defaultFeatures).forEach(key => {
        disabledMap[key] = false;
    })
    return disabledMap;
}

export function assembleConfig({structure, activeFeatures, includeDefaults=true}) {
    let config = cloneConfig(structure.defaultConfig);
    const defaultFeatures = includeDefaults ? getDefaultFeatureConfig()[structure.key] : {};
    const mergedActiveFeatures = {...defaultFeatures, ...activeFeatures}
    const availableFeatures = getAvailableFeaturesForStructure(structure.key, mergedActiveFeatures);

    try {
        availableFeatures.forEach(feature => {
            if (feature.parentFeature && !mergedActiveFeatures[feature.parentFeature]) return;
            if (mergedActiveFeatures[feature.key]) {
                Object.keys(feature.config ?? feature.providesConfig ?? {}).forEach(key => {
                    if (!(key in config) && !feature.missingConfigAllowed?.includes(key)) {
                        console.error('Feature key not found in config', {key, feature, config});
                    } else {
                        const featureParam = feature.config?.[key] ?? feature.providesConfig?.[key];
                        if (Array.isArray(featureParam)) {
                            config[key] = [...config[key], ...featureParam];
                        } else if (featureParam && featureParam.FIRST) {
                            config[key] = [...featureParam.FIRST, ...config[key]];
                        } else if (featureParam && featureParam.REPLACE) {
                            config[key] = featureParam.REPLACE;
                        } else if (featureParam !== undefined) {
                            config[key] = featureParam;
                        }
                    }
                });
                if (feature.defaultConfig ?? feature.receivesConfigDefaults) {                
                    config = {...config, ...feature.defaultConfig, ...feature.receivesConfigDefaults};
                }
            }
        })

        return config;
    } catch (e) {
        console.error('Error assembling config, using defaults', e);
        return cloneConfig(structure.defaultConfig ?? structure.receivesConfigDefaults);
    }
}

export function assembleScreenSet({structure, activeFeatures}) {
    const defaultFeatures = getDefaultFeatureConfig()[structure.key];
    const mergedActiveFeatures = {...defaultFeatures, ...activeFeatures} 
    const availableFeatures = getAvailableFeaturesForStructure(structure.key, mergedActiveFeatures);
   
    let screenSet = {...structure.subscreens};
    availableFeatures.forEach(feature => {
        if (mergedActiveFeatures[feature.key] && feature.subscreens) {
            screenSet = {...screenSet, ...feature.subscreens};
        }
    })
    return screenSet;
}

function cloneConfig(config) {
    config = {...config};
    Object.keys(config).forEach(key => {
        if (Array.isArray(config[key])) {
            config[key] = [...config[key]];
        }
    })
    return config;
}

export function flattenFeatureBlocks(featureList: FeatureMenuItem[], mergedActiveFeatures=null): Feature[] {
    const flattened: Feature[] = [];
    
    featureList.forEach(feature => {
        if (!feature) {
            console.error('feature is null', featureList);
            return;
        }
        
        if ('kind' in feature) {
            switch (feature.kind) {
                case 'composite':
                    flattened.push(feature.parent);
                    if (!mergedActiveFeatures || mergedActiveFeatures[feature.parent.key]) {
                        flattened.push(...flattenFeatureBlocks(feature.features, mergedActiveFeatures));
                    }
                    break;
                    
                case 'section':
                case 'chooseOne':
                    flattened.push(...flattenFeatureBlocks(feature.features, mergedActiveFeatures));
                    break;
            }
        } else {
            flattened.push(feature);
        }
    });
    
    return flattened;
}

export function getAvailableFeaturesForStructure(structureKey, mergedActiveFeatures) {
    const features = getFeatures();
    return flattenFeatureBlocks(features[structureKey] || [], mergedActiveFeatures);
}

export function getFeatureBlocks(structureKey) {
    const features = getFeatures();
    return features[structureKey] || [];
}

